import React from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {IconWrapper} from '@vio/atlas/src/components/designSystem/Link'
import {SkeletonLoader} from '@vio/atlas/src/components/designSystem/SkeletonLoader'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import minus from '@vio/atlas/src/utils/minus'
import {cssTheme} from '@vio/atlas-tokens'

import {aiReviewsMessages} from '../constants'

const CategoriesContainer = styled.div`
  display: flex;
  gap: ${cssTheme.layout.spacing.s300};
  margin-top: ${cssTheme.layout.spacing.s300};
  margin-left: ${minus(cssTheme.layout.spacing.s500)};
  margin-right: ${minus(cssTheme.layout.spacing.s500)};
  padding-left: ${cssTheme.layout.spacing.s500};
  padding-right: ${cssTheme.layout.spacing.s500};
  margin-bottom: ${cssTheme.layout.spacing.s400};
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

export const AiPoweredReviewsSummaryLoading = () => {
  const {formatMessage} = useIntl()
  const {isMobile} = useDeviceLayout()

  const categoriesSkeletons = new Array(6).fill(1)

  if (isMobile) {
    return (
      <>
        <Box>
          <Box
            padding="s600"
            borderRadius="lg"
            borderColor="border.neutral.secondary"
            borderWidth="bw100"
            borderStyle="solid"
          >
            <Box
              display="flex"
              gap="s200"
              alignItems="center"
              marginBottom="s100"
            >
              <Icon
                name="Sparkles"
                colorPath="content.brand.primary"
                size="lg"
              />

              <Text variant="titleL">
                {formatMessage(aiReviewsMessages.aiPoweredSummarySectionTitle)}
              </Text>
            </Box>
            <Box marginBottom="s400">
              <Text
                variant="bodyXS"
                colorPath="content.neutral.secondary"
                tag="div"
              >
                {formatMessage(
                  aiReviewsMessages.aiPoweredSummarySectionDescription
                )}
                <IconWrapper>
                  <Icon
                    name="Information"
                    size="xs"
                    colorPath="content.neutral.secondary"
                  />
                </IconWrapper>
              </Text>
            </Box>
            <Box display="flex" flexDirection="column" gap="s600">
              <div>
                <SkeletonLoader />
                <SkeletonLoader />
              </div>
              <div>
                <SkeletonLoader />
                <SkeletonLoader />
              </div>
            </Box>
          </Box>
        </Box>
        <CategoriesContainer>
          {categoriesSkeletons.map((_, index) => (
            <Box
              key={index}
              minWidth="160px"
              padding="s400"
              borderColor="border.neutral.secondary"
              borderWidth="bw100"
              borderStyle="solid"
              borderRadius="md"
            >
              <SkeletonLoader />
              <SkeletonLoader width={60} />
            </Box>
          ))}
        </CategoriesContainer>
      </>
    )
  }

  return (
    <Box
      borderColor="border.neutral.secondary"
      borderWidth="bw100"
      borderStyle="solid"
      padding="s600"
      borderRadius="lg"
    >
      <Box display="flex" gap="s200" alignItems="center" marginBottom="s100">
        <Icon name="Sparkles" colorPath="content.brand.primary" size="lg" />

        <Text variant="titleL">
          {formatMessage(aiReviewsMessages.aiPoweredSummarySectionTitle)}
        </Text>
      </Box>
      <Box display="flex" gap="s100" alignItems="center" marginBottom="s400">
        <Text variant="bodyXS" colorPath="content.neutral.secondary">
          {formatMessage(aiReviewsMessages.aiPoweredSummarySectionDescription)}
        </Text>
      </Box>

      <Box display="flex" flexDirection="column" gap="s600">
        <div>
          <SkeletonLoader />
          <SkeletonLoader />
        </div>
        <div>
          <SkeletonLoader />
          <SkeletonLoader />
        </div>
      </Box>

      <Box
        display="grid"
        marginTop="s600"
        gridTemplateColumns="1fr 1fr 1fr"
        gridTemplateRows="1fr  1fr"
        rowGap="s600"
        columnGap="s400"
      >
        {categoriesSkeletons.map((_, index) => (
          <div key={index}>
            <SkeletonLoader />
            <SkeletonLoader width={60} />
          </div>
        ))}
      </Box>
    </Box>
  )
}
